import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { enqueueSnackbar } from 'notistack';
import { Box, Chip, IconButton, Tooltip } from '@mui/material';
import LinkIcon  from '@mui/icons-material/Link';
import axiosInstance from '../../../../utils/axios';
import CustomDataGrid from '../../../../reusable-components/datagrid/CustomDataGrid';
import { getBookingSubmitTypeName } from './BookingTypesUtils';
import InvoiceBookingTypesToolbar from './InvoiceBookingTypesToolbar';
import BookingTypeForm from './BookingTypeForm';
import EditIconButton from '../../../../reusable-components/basic-buttons/EditIconButton';
import { ConfirmDialog } from '../../../../reusable-components/confirm-dialog';
import { BookingTypeSubmitRules } from '../../../../utils/globalEnums';
import BookingTypeConnectionForm from './BookingTypeConnectionForm';
import { getVendors } from '../../../../redux/slices/vendors';

export default function BookingTypesView() {
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  const [bookingTypes, setBookingTypes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [openForm, setOpenForm] = useState(false);
  const [editBookingType, setEditBookingType] = useState(null);
  const [connectBookingType, setConnectBookingType] = useState(null);

  useEffect(() => {
    setIsLoading(true);
    axiosInstance.get("Admin/InvoiceBookingType/BookingTypes")
      .then(res => {
        const bookingTypesData = (res.data ?? []).map(item => ({ ...item, submitType: getBookingSubmitTypeName(item.submitTypeId) })).sort((a, b) => a.name.localeCompare(b.name));
        const bookingTypeId = searchParams.get("id");
        if (bookingTypeId) {
          const bookingType = bookingTypesData.find(type => type.id === Number(bookingTypeId));
          if (bookingType) {
            const error = searchParams.get("error");
            bookingType.connectionSuccess = !!error;
            bookingType.connectionError = error;
            setConnectBookingType(bookingType);
          }
        }
        setBookingTypes(bookingTypesData);
        dispatch(getVendors());
      })
      .catch(() => enqueueSnackbar("Failed to load booking data", { variant: "error" }))
      .finally(() => setIsLoading(false));
  }, []);

  const deleteBookingType = (id) => {
    axiosInstance.post(`Admin/InvoiceBookingType/DeleteBookingType?id=${id}`)
      .then(() => {
        setBookingTypes(types => types.filter(type => type.id !== id));
        enqueueSnackbar("Booking type deleted", { variant: "success" });
      })
      .catch(() => enqueueSnackbar("Failed to delete booking type", { variant: "error" }))
      .finally(() => setIsLoading(false));
  }

  const openConnectionTest = (bookingType) => {
    if (bookingType.submitTypeId === BookingTypeSubmitRules.sendThroughIntacct || bookingType.submitTypeId === BookingTypeSubmitRules.sendThroughQuickbooks)
      setConnectBookingType(bookingType);
  }

  return <>
    <CustomDataGrid
      gridId="admin-invoice-booking-types"
      boxSX={{ height: 'calc(100vh - 260px)' }}
      data={bookingTypes}
      disableRowSelectionOnClick
      disableCellHighlight
      gridColumns={[
        { field: 'name', headerName: 'Name', minWidth: 150, flex: 1 },
        {
          field: 'facilities', headerName: 'Facilities', minWidth: 150, flex: 1, renderCell: (params) => <>
            {params.value?.length > 0 && <Box>{params.value[0]}</Box>}
            {params.value?.length > 1 && <Tooltip arrow placement="top" title={params.value.slice(1).join(", ")}>
              <Chip label={`+${params.value.length - 1}`} size="small" sx={{ ml: .7 }} />
            </Tooltip>}
          </>
        },
        { field: 'bulkSendFrequency', headerName: 'Bulk invoice send frequency', width: 200 },
        { field: 'isQue', headerName: 'Manual invoice submit type', width: 170, valueGetter: (params) => params.value ? "Sends to que" : "Sends immediately" },
        { field: 'submitType', headerName: 'Booking vendor type', width: 150 },
        {
          field: 'actions',
          headerName: 'Actions',
          width: 110,
          disableReorder: true,
          sortable: false,
          renderCell: (params) => <>
            {(params.row.submitTypeId === BookingTypeSubmitRules.sendThroughIntacct || params.row.submitTypeId === BookingTypeSubmitRules.sendThroughQuickbooks) && <Tooltip title={"Test Connection"} arrow>
              <IconButton edge="end" color="secondary" onClick={() => setConnectBookingType(params.row)} sx={{ p: 0, mr: 0.5 }}>
                <LinkIcon />
              </IconButton>
            </Tooltip>}
            <EditIconButton disableRipple onClick={() => { setEditBookingType(params.row); setOpenForm(true); }} />          
            <ConfirmDialog
              disableRipple
              icon="delete-outline"
              title="Delete"
              color="error"
              content={`Are you sure you want to delete ${params.row.name}?`}
              actionButton="Delete"
              action={() => deleteBookingType(params.row.id)}
              tooltip={"Delete"}
            />
          </>
        }
      ]}
      CustomLeftToolbar={() => <InvoiceBookingTypesToolbar setOpenForm={setOpenForm} />}
      isLoading={isLoading}
    />

    {openForm && <BookingTypeForm bookingType={editBookingType} setBookingTypes={setBookingTypes} close={(result) => { setOpenForm(false); setEditBookingType(null); openConnectionTest(result); }} />}
    {connectBookingType && <BookingTypeConnectionForm bookingType={connectBookingType} close={() => setConnectBookingType(null)} />}
   </>;
}